import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { createPortal } from 'react-dom';
import { X, ImagePlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../ThemeProvider';
import { businessTokens } from './businessTheme';
import { BusinessService } from '../../services/BusinessService';
import { BusinessStoreItem, STORE_ITEM_CAP } from '../../types/business';

/**
 * Add or edit one product.
 *
 * `price` is free text, matching the data model: the sale happens on the
 * business's own site, so "£25", "From $40" and "DM for price" are all things a
 * seller legitimately wants to say, and a numeric field would force all three
 * into a lie.
 */
export function BusinessStoreItemModal({
  isOpen,
  onClose,
  profileId,
  ownerId,
  editing,
  currentCount,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  profileId: string;
  ownerId: string;
  /** Null when adding. */
  editing: BusinessStoreItem | null;
  currentCount: number;
  onSaved: () => void;
}) {
  const { theme } = useTheme();
  const t = businessTokens(theme);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [purchaseLink, setPurchaseLink] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset per open so an edit never leaks fields into the next add.
  useEffect(() => {
    if (!isOpen) return;
    setTitle(editing?.title || '');
    setDescription(editing?.description || '');
    setPrice(editing?.price || '');
    setPurchaseLink(editing?.purchaseLink || '');
    setImageUrl(editing?.imageUrl || '');
  }, [isOpen, editing]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Pick an image.'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('That image is over 8MB. Pick a smaller one.'); return; }
    setUploading(true);
    try {
      setImageUrl(await BusinessService.uploadBusinessMedia(ownerId, file, 'product'));
    } catch {
      toast.error('That image would not upload. Try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const res = editing
      ? await BusinessService.updateStoreItem(profileId, editing.id, { title, description, price, imageUrl, purchaseLink })
      : await BusinessService.addStoreItem(profileId, { title, description, price, imageUrl, purchaseLink });
    setSaving(false);
    if (!res.success) { toast.error(res.error || 'Could not save that product.'); return; }
    toast.success(editing ? 'Product updated.' : 'Product added.');
    onSaved();
    onClose();
  };

  const atCap = !editing && currentCount >= STORE_ITEM_CAP;

  const inputStyle: React.CSSProperties = {
    width: '100%', background: t.inset, border: `1px solid ${t.border}`,
    borderRadius: 12, padding: '11px 13px', color: t.text, fontSize: 14,
    outline: 'none', fontFamily: 'inherit',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 700, color: t.text, margin: '14px 0 6px',
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 999997, background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 500, maxHeight: '88vh', overflowY: 'auto',
              background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20,
              padding: 20, boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
              <h2 style={{ margin: 0, flex: 1, fontSize: 18, fontWeight: 700, color: t.text }}>
                {editing ? 'Edit product' : 'Add a product'}
              </h2>
              <button
                type="button" onClick={onClose} aria-label="Close"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 4 }}
              >
                <X style={{ width: 18, height: 18 }} />
              </button>
            </div>

            {atCap ? (
              <p style={{ margin: '12px 0 0', fontSize: 14, color: t.danger }}>
                Your store is full at {STORE_ITEM_CAP} products. Remove one to add another.
              </p>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Product image"
                  style={{
                    position: 'relative', width: '100%', aspectRatio: '4 / 3', marginTop: 14,
                    borderRadius: 14, overflow: 'hidden', cursor: 'pointer', padding: 0,
                    background: imageUrl ? 'transparent' : t.inset,
                    border: `1px ${imageUrl ? 'solid' : 'dashed'} ${imageUrl ? t.border : t.accent}66`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {imageUrl && <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  {uploading ? (
                    <div
                      style={{
                        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <Loader2 style={{ width: 22, height: 22, color: '#fff' }} className="animate-spin" />
                    </div>
                  ) : !imageUrl && (
                    <div style={{ textAlign: 'center' }}>
                      <ImagePlus style={{ width: 24, height: 24, color: t.accent }} />
                      <p style={{ margin: '6px 0 0', fontSize: 12, color: t.textMuted }}>Add a product photo</p>
                    </div>
                  )}
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />

                <label style={labelStyle}>Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, 120))}
                  placeholder="What it is"
                  style={inputStyle}
                />

                <label style={labelStyle}>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 400))}
                  placeholder="Size, materials, delivery, whatever a buyer needs to know."
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                />

                <label style={labelStyle}>Price</label>
                <input
                  value={price}
                  onChange={(e) => setPrice(e.target.value.slice(0, 40))}
                  placeholder="$45, £30, From $20"
                  style={inputStyle}
                />

                <label style={labelStyle}>Purchase link</label>
                <input
                  value={purchaseLink}
                  onChange={(e) => setPurchaseLink(e.target.value)}
                  placeholder="yourshop.com/product"
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  style={inputStyle}
                />
                <p style={{ margin: '6px 0 0', fontSize: 11, color: t.textMuted }}>
                  Where this product is actually bought. Buyers leave AfroYap to complete the
                  purchase, and we ask them afterwards whether it went through.
                </p>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || uploading}
                  style={{
                    width: '100%', height: 48, borderRadius: 14, border: 'none', marginTop: 18,
                    background: t.buttonBg, color: t.buttonText, fontSize: 15, fontWeight: 700,
                    opacity: (saving || uploading) ? 0.6 : 1,
                    cursor: (saving || uploading) ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  {saving && <Loader2 style={{ width: 16, height: 16 }} className="animate-spin" />}
                  {editing ? 'Save changes' : 'Add to store'}
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
